import { GasTypeValue, Order, SavedAddress, SlotSource } from "./type";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";

// 商品價格 API 響應類型
export type GasPriceItem = {
  gpId: number | null;
  cisGpId: number | null;
  gasId: number;
  price: number;
  gasCylinderInfo: {
    gasId: number;
    kilogram: number;
    gasType: string;
  };
};

// 取得商品與價格列表
// v0.6 cutover：原本走 legacy /app-api/order/gas/price，但該 endpoint 預設 caller
// 是 cis owner（authCustomer.customer.customer_in_suppliers.supplier_id 直讀），
// household member（customer 無自己的 cis）會打到 NPE 500。
// 改打 /platform-order-drafts/bootstrap，flatten 全部 available_suppliers 的 gas_cylinders。
// bootstrap 走 ContextGuard → 統一從 context owner 解析 cis，member 也能用。
type BootstrapGasCylinder = {
  type: "gas" | "cis_gas";
  price_id: number;
  gas_id: number;
  gas_type: string;
  kilogram: number;
  unit_price: number;
};
type BootstrapSupplier = {
  supplier_id: string;
  gas_cylinders: BootstrapGasCylinder[];
};
type BootstrapApiResponse = {
  status: number;
  message: string;
  data: {
    available_suppliers: BootstrapSupplier[];
  };
  RF: string;
};

export const getGasPriceList = async (): Promise<GasPriceItem[]> => {
  const response = await api.get<BootstrapApiResponse>(
    "/app-api/platform-order-drafts/bootstrap",
  );
  const suppliers = response.data?.data?.available_suppliers ?? [];
  return suppliers.flatMap((s) =>
    (s.gas_cylinders ?? []).map<GasPriceItem>((c) => ({
      gpId: c.type === "gas" ? c.price_id : null,
      cisGpId: c.type === "cis_gas" ? c.price_id : null,
      gasId: c.gas_id,
      price: c.unit_price,
      gasCylinderInfo: {
        gasId: c.gas_id,
        kilogram: c.kilogram,
        gasType: c.gas_type,
      },
    })),
  );
};

type DraftItem = {
  type: string;
  gas_type: string;
  price_id: number;
  number: number;
  delivery_id: number;
  unit_price: number;
  gas_kilogram: number;
};

export type DraftOrderResponse = {
  draft_id: string;
  line_user_id: string;
  supplier_id: string;
  draft_note: string;
  status: string;
  created_at: string;
  updated_at: string;
  ttl: number;
  customer_infos: {
    customer_name: string;
    customer_phone: string;
    tax_id_number: string;
    note: string;
    invoice_carrier: string;
    carrier_type: string;
  };
  address_infos: {
    address_id: number;
    customer_address_id: number;
    address: string;
    latitude: number;
    longitude: number;
  };
  order_infos: {
    original_order_id: string;
    cis_id: string;
    contact_phone: string;
    tax_id_number: string;
    delivery_time_stamp: string | Record<string, never>;
    /** 後端回傳「預約配送」或「立即配送」 */
    delivery_type_enum?: string;
    // 新版若後端有回傳 slot 資訊，前端可直接使用
    slot_id?: number | null;
    slot_source?: SlotSource | null;
    courier_id: string;
    discount: number;
    gas_discount: number;
    order_status: string;
    note: string;
    /** 電子發票載具（部分 API 回傳於 order_infos） */
    invoice_carrier?: string;
  };
  item_list: DraftItem[];
};

const toGasType = (value: string): GasTypeValue => {
  // 後端目前用中文描述氣別，例如「液化氣」「純丙烷」
  if (value.includes("丙烷")) return "propane";
  return "gas";
};

export const mapDraftToOrder = (draft: DraftOrderResponse): {
  order: Order;
  savedAddresses: SavedAddress[];
  currentMonth: Date | null;
} => {
  const info = draft.order_infos;
  const deliveryTypeEnum = info?.delivery_type_enum;

  // 解析 delivery_time_stamp
  const deliveryTimestamp = info?.delivery_time_stamp;
  const isValidTimestamp =
    deliveryTimestamp &&
    typeof deliveryTimestamp === "string" &&
    deliveryTimestamp.trim() !== "" &&
    !deliveryTimestamp.startsWith("{") &&
    !isNaN(new Date(deliveryTimestamp).getTime());

  const deliveryDate = isValidTimestamp ? new Date(deliveryTimestamp) : null;
  const deliveryDateStr = deliveryDate && !isNaN(deliveryDate.getTime())
    ? `${deliveryDate.getFullYear()}-${String(deliveryDate.getMonth() + 1).padStart(2, "0")}-${String(deliveryDate.getDate()).padStart(2, "0")}`
    : null;

  const hasBackendSlot =
    typeof info?.slot_id === "number" &&
    (info.slot_source === "weekly" || info.slot_source === "special");
  const hasTime = hasBackendSlot || isValidTimestamp;

  // slot_id 明確為 null（非 undefined）→ 後端已清除時段，通常為立即配送的特徵
  const slotIdExplicitlyNull =
    info != null && Object.prototype.hasOwnProperty.call(info, "slot_id") && info.slot_id === null;
  // 立即配送：
  //   1. delivery_type_enum 明確為「立即配送」
  //   2. delivery_type_enum 缺席且 slot_id 明確為 null（後端已清除時段，代表上次儲存的是立即配送）
  const isImmediate =
    deliveryTypeEnum === "立即配送" || (deliveryTypeEnum !== "預約配送" && slotIdExplicitlyNull);
  // 有時間且為「預約配送」→ 預選預約配送 + 日期／時段 mapping
  const isScheduledWithTime = !isImmediate && (deliveryTypeEnum === "預約配送" || !deliveryTypeEnum) && hasTime;
  // 沒時間（且非立即配送）→ 預約配送但不預選日期／時段
  const isScheduledNoTime = !isImmediate && !hasTime;

  const deliverySlotId = isScheduledWithTime && typeof info?.slot_id === "number" ? info.slot_id : null;
  const deliverySlotSource =
    isScheduledWithTime && (info?.slot_source === "weekly" || info?.slot_source === "special")
      ? info.slot_source
      : null;

  const customerAddressId = String(draft.address_infos.customer_address_id);
  const savedAddresses: SavedAddress[] = [
    {
      id: customerAddressId,
      address_id: draft.address_infos.address_id,
      address: draft.address_infos.address,
      isPopular: true,
      lat: draft.address_infos.latitude,
      lng: draft.address_infos.longitude,
    },
  ];

  const finalDeliveryType = isImmediate ? "immediate" : "scheduled";
  const finalDate = isScheduledWithTime ? deliveryDateStr : null;
  const finalSlotId = isScheduledWithTime ? deliverySlotId : null;
  const finalSlotSource = isScheduledWithTime ? deliverySlotSource : null;
  const finalCurrentMonth = finalDate && deliveryDate ? new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), 1) : null;

  return {
    order: {
      items: draft.item_list.map((item, index) => ({
        id: index + 1,
        weight: String(item.gas_kilogram || 20),
        type: toGasType(item.gas_type),
        quantity: item.number || 1,
        priceId: item.price_id,
        unitPrice: item.unit_price,
        gasKilogram: item.gas_kilogram,
        gasType: item.gas_type,
        isCisGas: item.type === "cis_gas",
      })),
      addressId: customerAddressId,
      deliveryType: finalDeliveryType,
      deliveryDate: finalDate,
      deliverySlotId: finalSlotId,
      deliverySlotSource: finalSlotSource,
      deliverySlotLabel: null,
      taxId:
        draft.customer_infos?.tax_id_number ||
        draft.order_infos?.tax_id_number ||
        "",
      carrier: draft.customer_infos?.invoice_carrier || draft.order_infos?.invoice_carrier || "",
      phone:
        draft.order_infos?.contact_phone ||
        draft.customer_infos?.customer_phone ||
        "",
      note: draft.order_infos?.note || draft.draft_note || "",
    },
    savedAddresses,
    currentMonth: finalCurrentMonth,
  };
};

// 取得所有 draft IDs
export type DraftIdListResponse = {
  status: number;
  message: string;
  data: {
    draft_id_list: string[];
  };
  RF: string;
};

export const getAllDraftIds = async (): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const asyncRequest = async () => api.get<DraftIdListResponse>("/app-api/quick-order/draft/line-user");

    const successCallback = async (response: AxiosResponse<DraftIdListResponse>) => {
      try {
        // API 返回格式: { status, message, data: { draft_id_list: [] }, RF }
        // 嘗試多種可能的數據結構
        let draftIdList: string[] = [];

        if (response.data?.data?.draft_id_list) {
          // 標準格式: { status, message, data: { draft_id_list: [] }, RF }
          draftIdList = response.data.data.draft_id_list;
        } else if ((response.data as any)?.draft_id_list) {
          // 直接格式: { draft_id_list: [] }
          draftIdList = (response.data as any).draft_id_list;
        } else if (Array.isArray(response.data)) {
          // 數組格式: []
          draftIdList = response.data;
        }

        resolve(draftIdList);
      } catch (err) {
        reject(err);
      }
    };

    const errorCallback = (error: any) => {
      reject(error);
    };

    handleRequest({
      asyncRequest,
      successCallback,
      errorCallback,
    });
  });
};

/** 草稿列表單筆摘要（含建立時間、狀態，供列表頁顯示） */
export type DraftListItem = {
  draftId: string;
  created_at?: string;
  order_status?: string;
};

/** 取得草稿 ID 列表並並行取得每筆的 created_at、order_status（單筆失敗不影響其他） */
export const getDraftListWithDetails = async (): Promise<DraftListItem[]> => {
  const ids = await getAllDraftIds();
  const results = await Promise.all(
    ids.map(async (draftId) => {
      try {
        const draft = await getDraftOrder(draftId);
        return {
          draftId: draft.draft_id,
          created_at: draft.created_at,
          order_status: draft.order_infos?.order_status,
        };
      } catch {
        return { draftId, created_at: undefined, order_status: undefined };
      }
    })
  );
  return results;
};

// 取消草稿訂單的 API 響應格式
type CancelDraftOrderApiResponse = {
  status: number;
  message: string;
  data: {
    draft_id: string;
  };
  RF: string;
};

// 取消草稿訂單
export const cancelDraftOrder = async (draftId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const asyncRequest = async () =>
      api.delete<CancelDraftOrderApiResponse>(`/app-api/quick-order/draft/${draftId}/cancel`);

    const successCallback = async (response: AxiosResponse<CancelDraftOrderApiResponse>) => {
      // API 返回格式: { status, message, data: { draft_id }, RF }
      const cancelledDraftId = response.data?.data?.draft_id || draftId;
      resolve(cancelledDraftId);
    };

    const errorCallback = (error: any) => {
      reject(error);
    };

    handleRequest({
      asyncRequest,
      successCallback,
      errorCallback,
    });
  });
};

// 取得單一 draft 訂單的 API 響應格式
type DraftOrderApiResponse = {
  status: number;
  message: string;
  data: DraftOrderResponse;
  RF: string;
};

// 取得單一 draft 訂單（直接使用 api.get，確保 Promise 正確 resolve/reject）
export const getDraftOrder = async (
  draftId: string,
  signal?: AbortSignal
): Promise<DraftOrderResponse> => {
  const response = await api.get<DraftOrderApiResponse>(
    `/app-api/quick-order/draft/${draftId}`,
    { signal }
  );
  const body = response.data as any;
  // 後端可能回傳 HTTP 200 但 body 內 status 表示錯誤（如 404）或 message 為找不到草稿
  const bodyStatus = body?.status;
  const bodyMessage = body?.message ?? "";
  if (
    (typeof bodyStatus === "number" && bodyStatus >= 400) ||
    String(bodyMessage).includes("Draft order not found") ||
    String(bodyMessage).includes("找不到草稿")
  ) {
    const err: any = new Error(bodyMessage || "找不到草稿訂單");
    err.response = { data: body };
    throw err;
  }
  const draftData = response.data?.data ?? response.data;
  if (!draftData || typeof draftData !== "object") {
    throw new Error("草稿資料為空，請重新整理頁面再試。");
  }
  // 必須具備草稿必要欄位，否則視為無效/找不到（避免快速修改時間頁誤顯示表單）
  if (!draftData.draft_id || !draftData.order_infos) {
    const err: any = new Error(bodyMessage || "找不到草稿訂單");
    err.response = { data: body };
    throw err;
  }
  return draftData as DraftOrderResponse;
};

// 更新草稿訂單的 API 響應格式
type UpdateDraftOrderApiResponse = {
  status: number;
  message: string;
  data: DraftOrderResponse;
  RF: string;
};

// 更新草稿訂單
export const updateDraftOrder = async (
  draftId: string,
  payload: any
): Promise<DraftOrderResponse> => {
  return new Promise((resolve, reject) => {
    const asyncRequest = async () =>
      api.put<UpdateDraftOrderApiResponse>(`/app-api/quick-order/draft/${draftId}`, payload);

    const successCallback = async (response: AxiosResponse<UpdateDraftOrderApiResponse>) => {
      // API 返回格式: { status, message, data: { draft_id, ... }, RF }
      const draftData = response.data?.data || response.data;
      resolve(draftData);
    };

    const errorCallback = (error: any) => {
      reject(error);
    };

    handleRequest({
      asyncRequest,
      successCallback,
      errorCallback,
    });
  });
};

// 可預約時段的類型定義
export type AvailableSlot = {
  slot_id: number;
  slot_source: "weekly" | "special";
  start_time: string;
  end_time: string;
  available_capacity: number;
};

export type AvailableSlotsDay = {
  delivery_date: string;
  is_closed_all_day: boolean;
  slots: AvailableSlot[];
};

type AvailableSlotsApiResponse = {
  status: number;
  message: string;
  data: AvailableSlotsDay[];
  RF: string;
};

// 獲取可預約時段
export const getAvailableSlots = async (): Promise<AvailableSlotsDay[]> => {
  const response = await api.get<AvailableSlotsApiResponse>("/app-api/business-hours/available-slots");
  const list = response.data?.data;
  return Array.isArray(list) ? list : [];
};

// ---------- 取得客戶地址列表 ----------
export type CustomerAddressListItem = {
  customer_address_id: number;
  customer_id: number;
  city: string | null;
  site_id: string | null;
  road: string | null;
  address_number: string | null;
  floor: string | null;
  room: string | null;
  extra: string | null;
  latitude: number | null;
  longitude: number | null;
  customerDeliveryList: unknown[];
};

type CustomerAddressListResponse = {
  status: number;
  message: string;
  data: CustomerAddressListItem[];
  RF?: string;
};

/** GET /app-api/delivery/address 取得客戶地址列表 */
export const getCustomerAddressList = async (): Promise<CustomerAddressListItem[]> => {
  const response = await api.get<CustomerAddressListResponse>("/app-api/delivery/address");
  const list = response.data?.data;
  return Array.isArray(list) ? list : [];
};

/** 將 API 地址項目轉成 SavedAddress（給 DeliverAddressCard 用） */
export function mapCustomerAddressToSaved(item: CustomerAddressListItem): SavedAddress {
  const id = String(item.customer_address_id);
  const parts = [
    item.city,
    item.site_id,
    item.road,
    item.address_number,
    item.floor,
    item.room,
  ].filter(Boolean) as string[];
  const base = parts.join("") || "未填寫地址";
  const address = item.extra?.trim() ? `${base} ${item.extra.trim()}` : base;
  return {
    id,
    address,
    isPopular: false,
    lat: item.latitude ?? 23.7554174,
    lng: item.longitude ?? 120.4992749,
    // 結構化欄位：直接保留 API 回傳值，編輯時無需再次正規化
    city: item.city ?? undefined,
    site_id: item.site_id ?? undefined,
    road: item.road ?? undefined,
    address_number: item.address_number ?? undefined,
    floor: item.floor ?? undefined,
    room: item.room ?? undefined,
    extra: item.extra?.trim() || undefined,
  };
}

// ---------- 建立客戶地址 ----------
export type CreateAddressPayload = {
  addressInfos: {
    city?: string;
    site_id?: string;
    road?: string;
    address_number?: string;
    floor?: string;
    room?: string;
    extra?: string;
    latitude?: number;
    longitude?: number;
    addressId?: number; // 選填，綁定現有地址
  };
};

/** 從台灣完整地址字串解析出結構化欄位（縣市、鄉鎮市區、路、號、樓等） */
export function parseTaiwanAddress(full: string): {
  city: string;
  site_id: string;
  road: string;
  address_number: string;
  floor: string | null;
  room: string | null;
  extra: string;
} {
  const t = full.trim();
  let city = "";
  let site_id = "";
  let road = "";
  let address_number = "";
  let floor: string | null = null;
  let room: string | null = null;
  let extra = "";

  const cityMatch = t.match(/^(.+?[縣市])/);
  if (cityMatch) {
    city = cityMatch[1];
    const afterCity = t.slice(cityMatch[0].length);
    const siteMatch = afterCity.match(/^(.+?[區鄉鎮市])/);
    if (siteMatch) {
      site_id = siteMatch[1];
      const afterSite = afterCity.slice(siteMatch[0].length);
      const roadMatch = afterSite.match(/^(.+?[路街巷])/);
      if (roadMatch) {
        road = roadMatch[1];
        const afterRoad = afterSite.slice(roadMatch[0].length);
        const numMatch = afterRoad.match(/^(\d+號?)/);
        if (numMatch) {
          address_number = numMatch[1].endsWith("號") ? numMatch[1] : numMatch[1] + "號";
          const rest = afterRoad.slice(numMatch[0].length);
          const floorMatch = rest.match(/^(\d*樓?)/);
          if (floorMatch && floorMatch[1]) floor = floorMatch[1];
          const afterFloor = floorMatch && floorMatch[0] ? rest.slice(floorMatch[0].length) : rest;
          const roomMatch = afterFloor.match(/^之?(\d+)室?/);
          if (roomMatch) room = roomMatch[1];
          const afterRoom = roomMatch ? afterFloor.slice(roomMatch[0].length) : afterFloor;
          extra = afterRoom.trim();
        } else {
          road = road + afterRoad;
        }
      } else {
        road = afterSite;
      }
    } else {
      road = afterCity;
    }
  } else {
    city = t;
  }

  return {
    city: city || t,
    site_id,
    road,
    address_number,
    floor,
    room,
    extra,
  };
}

type RawCreateAddressResponse = {
  data?: {
    customer_address_id?: number;
    customerAddressResult?: {
      customer_address_id?: number;
      identifiers?: Array<{ customer_address_id?: number }>;
    };
    customer_address_result?: {
      customer_address_id?: number;
      identifiers?: Array<{ customer_address_id?: number }>;
    };
    addressBindingResult?: {
      address_binding_id?: number;
      customer_address_id?: number;
      address_id?: number;
      identifiers?: Array<{ address_binding_id?: number; customer_address_id?: number; address_id?: number }>;
    };
    address_binding_result?: {
      address_binding_id?: number;
      customer_address_id?: number;
      address_id?: number;
      identifiers?: Array<{ address_binding_id?: number; customer_address_id?: number; address_id?: number }>;
    };
  };
  customer_address_id?: number;
  customerAddressResult?: {
    customer_address_id?: number;
    identifiers?: Array<{ customer_address_id?: number }>;
  };
  customer_address_result?: {
    customer_address_id?: number;
    identifiers?: Array<{ customer_address_id?: number }>;
  };
  addressBindingResult?: {
    address_binding_id?: number;
    customer_address_id?: number;
    address_id?: number;
    identifiers?: Array<{ address_binding_id?: number; customer_address_id?: number; address_id?: number }>;
  };
  address_binding_result?: {
    address_binding_id?: number;
    customer_address_id?: number;
    address_id?: number;
    identifiers?: Array<{ address_binding_id?: number; customer_address_id?: number; address_id?: number }>;
  };
};

export type CreateAddressResponse = {
  customerAddressResult: {
    customer_address_id: number;
  };
  addressBindingResult?: {
    address_binding_id: number;
    customer_address_id: number;
    address_id: number;
  };
};

/** POST /app-api/delivery/address 建立客戶地址 */
export const createCustomerAddress = async (
  payload: CreateAddressPayload
): Promise<CreateAddressResponse> => {
  const response = await api.post<RawCreateAddressResponse>("/app-api/delivery/address", payload);
  const body = response?.data as Record<string, unknown> | undefined;
  const resolved = (body?.data ?? body) as Record<string, unknown> | undefined;
  const customerResult =
    (resolved?.customerAddressResult ?? resolved?.customer_address_result) as
      | { identifiers?: Array<{ customer_address_id?: number }>; customer_address_id?: number }
      | undefined;
  const bindingResult =
    (resolved?.addressBindingResult ?? resolved?.address_binding_result) as
      | {
          identifiers?: Array<{ address_binding_id?: number; customer_address_id?: number; address_id?: number }>;
          address_binding_id?: number;
          customer_address_id?: number;
          address_id?: number;
        }
      | undefined;

  const customerId =
    customerResult?.identifiers?.[0]?.customer_address_id ??
    customerResult?.customer_address_id ??
    (body?.data as Record<string, unknown> | undefined)?.customer_address_id ??
    body?.customer_address_id;

  if (typeof customerId === "number" && !Number.isNaN(customerId)) {
    return {
      customerAddressResult: {
        customer_address_id: customerId,
      },
      addressBindingResult: bindingResult
        ? {
            address_binding_id:
              bindingResult?.identifiers?.[0]?.address_binding_id ?? bindingResult.address_binding_id ?? 0,
            customer_address_id:
              bindingResult?.identifiers?.[0]?.customer_address_id ?? bindingResult.customer_address_id ?? customerId,
            address_id:
              bindingResult?.identifiers?.[0]?.address_id ?? bindingResult.address_id ?? 0,
          }
        : undefined,
    } as CreateAddressResponse;
  }

  throw new Error("建立地址失敗，回傳格式異常");
};

/** 建立地址 API 錯誤時，轉成給使用者看的訊息（含「無法配送此區域」） */
export function getCreateAddressErrorMessage(err: any): string {
  const raw = err?.response?.data?.message ?? err?.message ?? "";
  const rawStr = Array.isArray(raw) ? raw.join(" ") : String(raw);
  if (rawStr.includes("Delivery to") && rawStr.includes("is not allowed")) {
    return "該瓦斯行無法配送此區域，請選擇其他地址。";
  }
  return rawStr || "建立地址失敗，請稍後再試。";
}

/** 檢查所選時段是否為「當日已過」：用於送出前防呆 */
export function isSelectedSlotPastForToday(
  availableSlots: AvailableSlotsDay[],
  deliveryDate: string | null,
  slotId: number | null
): boolean {
  if (!deliveryDate || slotId == null) return false;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (deliveryDate !== todayStr) return false;
  const dayData = availableSlots.find((d) => d.delivery_date === deliveryDate);
  const slot = dayData?.slots?.find((s) => s.slot_id === slotId);
  if (!slot?.end_time) return false;
  const parts = slot.end_time.split(":").map(Number);
  const slotEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parts[0] ?? 0, parts[1] ?? 0, 0);
  return slotEnd <= now;
}

/** 依 API 的 is_closed_all_day 簡單判斷該日是否公休 */
export function isDeliveryDayClosed(
  availableSlots: AvailableSlotsDay[],
  deliveryDate: string
): boolean {
  const dayData = availableSlots.find((d) => d.delivery_date === deliveryDate);
  return dayData?.is_closed_all_day === true;
}

/** 檢查所選日期是否為公休日：用於送出前防呆 */
export function isSelectedDateClosed(
  availableSlots: AvailableSlotsDay[],
  deliveryDate: string | null
): boolean {
  if (!deliveryDate) return false;
  return isDeliveryDayClosed(availableSlots, deliveryDate);
}

/** QuickOrder 禁止修改規則：依後端錯誤 message/code 回傳對應中文提示 */
export function getQuickOrderErrorMessage(err: any): string {
  const message = err?.response?.data?.message ?? err?.message ?? "";
  const code = err?.response?.data?.error_code ?? err?.response?.data?.code ?? "";
  const rawStr = Array.isArray(message) ? message.join(" ") : String(message);
  const codeStr = String(code).toLowerCase();
  const combined = `${rawStr} ${codeStr}`;

  if (
    rawStr.includes("訂單完成") ||
    rawStr.includes("配送作業已結束") ||
    codeStr.includes("delivery_completed") ||
    codeStr.includes("order_completed")
  ) {
    return "實際配送作業已結束，無法再調整內容。";
  }
  if (
    rawStr.includes("已下訂") ||
    rawStr.includes("尚未配送") ||
    rawStr.includes("配送中") ||
    rawStr.includes("訂單已取消") ||
    codeStr.includes("order_submitted") ||
    codeStr.includes("not_editable")
  ) {
    return "訂單已完成下訂，無法再修改。";
  }
  if (
    rawStr.includes("時段") ||
    rawStr.includes("已過期") ||
    rawStr.includes("額滿") ||
    rawStr.includes("公休") ||
    codeStr.includes("slot") ||
    codeStr.includes("unavailable")
  ) {
    return "所選配送時間已過期、已額滿或該日公休，請重新選擇。";
  }
  if (
    rawStr.includes("立即配送") ||
    rawStr.includes("營業時間") ||
    codeStr.includes("immediate") ||
    codeStr.includes("business_hours")
  ) {
    return "目前無法使用立即配送，請選擇預約配送或其它時段。";
  }
  if (
    rawStr.includes("gas_price") ||
    rawStr.includes("cis_gas_price") ||
    rawStr.includes("查無") ||
    rawStr.includes("價格") ||
    codeStr.includes("price_not_found")
  ) {
    return "查無對應價格，無法修改該項目。";
  }
  // 草稿訂單找不到（與編輯訂單頁相同提示）
  if (
    rawStr.includes("Draft order not found") ||
    rawStr.includes("找不到草稿") ||
    codeStr.includes("draft_not_found")
  ) {
    return "找不到草稿訂單。此訂單可能已被取消、過期或連結不正確，請返回草稿列表重新選擇。";
  }

  return rawStr?.trim() || "操作失敗，請稍後再試。";
}

/** PUT 更新地址的回應（後端可能軟刪除原地址並建立新筆，id 在 identifiers[0]） */
export type UpdateAddressResponse = {
  data?: {
    customer_address_id?: number;
    customerAddressResult?: {
      identifiers?: Array<{ customer_address_id: number }>;
    };
    addressBindingResult?: {
      identifiers?: Array<{ customer_address_id: number; address_id: number }>;
    };
  };
  customer_address_id?: number;
  customerAddressResult?: { identifiers?: Array<{ customer_address_id: number }> };
  addressBindingResult?: {
    identifiers?: Array<{ customer_address_id: number; address_id: number }>;
  };
};

export type UpdateAddressResult = {
  customerAddressId: number;
  addressId?: number;
};

/** PUT /app-api/delivery/address/{customerAddressId} 更新客戶地址；回傳有效的 customer_address_id 與 address_id（可能為新 id） */
export const updateCustomerAddress = async (
  customerAddressId: string | number,
  payload: CreateAddressPayload
): Promise<UpdateAddressResult> => {
  const response = await api.put<UpdateAddressResponse>(
    `/app-api/delivery/address/${customerAddressId}`,
    payload
  );
  const status = response?.status ?? 0;
  if (status < 200 || status > 299) {
    throw new Error("更新地址失敗，請稍後再試。");
  }
  const body = response?.data as Record<string, unknown> | undefined;
  // 後端回傳在 data 裡，且 id 在 identifiers[0] 中
  const resolved = (body?.data ?? body) as Record<string, unknown> | undefined;
  const customerResult =
    (resolved?.customerAddressResult ?? resolved?.customer_address_result) as
      | { identifiers?: Array<{ customer_address_id?: number }>; customer_address_id?: number }
      | undefined;
  const bindingResult =
    (resolved?.addressBindingResult ?? resolved?.address_binding_result) as
      | { identifiers?: Array<{ customer_address_id?: number; address_id?: number }>; address_id?: number }
      | undefined;
  const customerIdFromIdentifiers = customerResult?.identifiers?.[0]?.customer_address_id;
  const bindingFromIdentifiers = bindingResult?.identifiers?.[0];
  const newCustomerId =
    customerIdFromIdentifiers ??
    customerResult?.customer_address_id ??
    (body?.data as Record<string, unknown>)?.customer_address_id ??
    body?.customer_address_id;
  const newAddressId =
    bindingFromIdentifiers?.address_id ?? bindingResult?.address_id;
  const idNum = typeof customerAddressId === "string" ? parseInt(customerAddressId, 10) : customerAddressId;
  const customerId =
    typeof newCustomerId === "number" && !Number.isNaN(newCustomerId) ? newCustomerId : idNum;
  return {
    customerAddressId: customerId,
    addressId: typeof newAddressId === "number" && !Number.isNaN(newAddressId) ? newAddressId : undefined,
  };
};

// ---------- 快速修改配送時間 ----------
export type SetDraftDeliveryPayload = {
  delivery_type_enum: "預約配送" | "立即配送";
  delivery_date: string;
  slot_id?: number;
  slot_source?: "weekly" | "special";
};

/** PATCH /app-api/quick-order/draft/{draft_id}/delivery 僅設定草稿配送時間 */
export const setDraftDeliveryTime = async (
  draftId: string,
  payload: SetDraftDeliveryPayload
): Promise<void> => {
  await api.patch(`/app-api/quick-order/draft/${draftId}/delivery`, payload);
};
